import type { JSX, VoidComponent } from "solid-js";
import { createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import { IconButton } from "../button/Button";
import { useTranslation } from "../../state/i18n";
import { isNil, uniqueId } from "lodash-es";
import { FocusTrap } from "@ark-ui/solid";
import autoResizeInput from "../../utils/autoResizeInput";
import type { GenericSchema } from "valibot";
import { pipe, safeParse, string, trim } from "valibot";
import { requireText } from "../../utils/validation/base";

type InlineEditorProps<T extends string | undefined> = {
  value: string;
  setValue: (value: T) => void;
  label: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onCancel?: () => void;
  maxLength: number;
  displayView?: () => JSX.Element;
  schema?: GenericSchema<T>;
};

const createDefaultSchema = (max: number) => pipe(string(), trim(), requireText(max));

// Note: ARK-ui (Editable) was not used because it can't handle our UX flow
const InlineEditor = <T extends string | undefined = string>(props: InlineEditorProps<T>) => {
  const schema = createMemo(() => props.schema ?? createDefaultSchema(props.maxLength));

  const Label = (
    <Show when={props.displayView} fallback={props.value}>
      {/* oxlint-disable-next-line better-tailwindcss/no-restricted-classes -- one extra pixel to account for border when opening/closing inline editor */}
      <div class="ml-[5px]">{props.displayView!()}</div>
    </Show>
  );

  const confirmEdit = (value: string) => {
    const result = safeParse(schema(), value);
    if (result.success && result.output !== props.value) {
      props.setValue(result.output as T);
    }

    props.setOpen(false);
  };

  const cancelEdit = () => {
    props.setOpen(false);
    props.onCancel?.();
  };

  return (
    <Show when={props.open} fallback={Label}>
      <Editor
        initialValue={props.value}
        confirm={confirmEdit}
        cancel={cancelEdit}
        label={props.label}
        maxLength={props.maxLength}
      />
    </Show>
  );
};

type EditorProps = {
  initialValue: string;
  confirm: (value: string) => void;
  cancel: () => void;
  label: string;
  maxLength?: number;
};

const Editor: VoidComponent<EditorProps> = (props) => {
  const { t } = useTranslation();
  const [value, setValue] = createSignal(props.initialValue);

  const id = uniqueId();

  let rootField!: HTMLDivElement;
  let inputRef!: HTMLInputElement;

  const handleOutsidePointerDown = (e: MouseEvent) => {
    if (isNil(e.target)) {
      return;
    }

    if (!rootField.contains(e.target as Node) || rootField === e.target) {
      props.confirm(value());
    }
  };

  const handleKeyboard = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      props.confirm(value());
    } else if (e.key === "Escape") {
      props.cancel();
    }
  };

  onMount(() => {
    document.addEventListener("pointerdown", handleOutsidePointerDown);
  });

  onCleanup(() => {
    document.removeEventListener("pointerdown", handleOutsidePointerDown);
  });

  // Automatically resize input based on content, can be removed when `field-sizing` CSS property has enough browser support
  onMount(() => {
    const cleanup = autoResizeInput(inputRef);

    onCleanup(() => {
      cleanup?.();
    });
  });

  return (
    <FocusTrap class="flex w-full items-center gap-1" ref={rootField}>
      <label for={id} class="sr-only">
        {props.label}
      </label>
      <input
        id={id}
        ref={inputRef}
        type="text"
        maxLength={props.maxLength}
        value={value()}
        onKeyDown={handleKeyboard}
        onInput={(e) => setValue(e.target.value)}
        class="max-w-full min-w-20 rounded-sm border border-default px-1 text-primary outline-default focus-visible:outline-default"
      />
      <IconButton
        icon="check"
        aria-label={t(($) => $.CONFIRM_ARIA)}
        size="medium"
        variant="tertiary"
        onClick={() => {
          props.confirm(value());
        }}
      />
      <IconButton
        icon="x"
        aria-label={t(($) => $.CANCEL_ARIA)}
        size="medium"
        variant="tertiary"
        onClick={props.cancel}
      />
    </FocusTrap>
  );
};

export default InlineEditor;
